import React from 'react';
import classNames from 'clsx';
import { Tooltip } from 'elmo-elements';
import { FeatureFlag } from 'element/feature-flags-components';
import { RosterNavWrapper } from '../RosterNavWrapper';
import { DayWrapper } from './DayWrapper';
import { DutyDisplay } from 'state/Roster/DutyRequirements/types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import './RosterWeekNavigation.scss';

export type Props = {
  days: RosterWeekNavigationDay[];
};

export type RosterWeekNavigationDay = {
  title: string;
  current: boolean;
  path?: string;
  onClick?: () => void;
  duties?: DutyDisplay[];
};

export const RosterWeekNavigation = ({ days }: Props) => (
  <RosterNavWrapper>
    <div className="roster-week-navigation">
      <div className="roster-week-navigation__days">
        {days.map(
          ({ current, title, path, onClick, duties }: RosterWeekNavigationDay, dayIndex) => (
            <DayWrapper
              key={dayIndex}
              path={path}
              className="roster-week-navigation__day"
              onClick={onClick}
            >
              <span
                className={classNames('roster-week-navigation__label', {
                  current
                })}
              >
                {title}
              </span>

              {/* Display duty requirements - behind 'duties' feature flag */}
              <FeatureFlag name="duties">
                <FeatureFlag.On>
                  {duties && duties.length > 0 && (
                    <div className="roster-week-navigation__duties">
                      {duties.map((duty) => {
                        const ratioText = `${duty.currentStaff}/${duty.totalPersonnel}`;
                        
                        // Build multi-line tooltip content as React element
                        const tooltipContent = (
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '14px', borderBottom: '1px solid rgba(255, 255, 255, 0.3)', paddingBottom: '6px' }}>
                              {duty.dutyName}
                            </div>
                            {duty.periodDetails.map((period, index) => {
                              // Use colors matching badge styles instead of emoji
                              const statusColor = period.isMet ? '#16a34a' : period.current === 0 ? '#dc2626' : '#ea580c';

                              return (
                                <div 
                                  key={index} 
                                  style={{ 
                                    fontSize: '13px', 
                                    margin: '6px 0', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '8px',
                                    lineHeight: 1.5
                                  }}
                                >
                                  
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <AccessTimeIcon style={{ fontSize: '14px', opacity: 0.8 }} />
                                    <span>{period.timeRange}</span>
                                  </span>
                                  <span style={{ color: statusColor, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <PersonIcon style={{ fontSize: '14px', opacity: 0.8 }} />
                                    <span>{period.current}/{period.required}</span>
                                  </span>
                                </div>
                              );
                            })}
                            <div style={{ 
                              fontWeight: 600, 
                              marginTop: '8px', 
                              paddingTop: '6px', 
                              borderTop: '1px solid rgba(255, 255, 255, 0.3)', 
                              fontSize: '13px' 
                            }}>
                              Total: {ratioText}
                            </div>
                          </div>
                        );
                        
                        return (
                          <Tooltip
                            key={duty.dutyId}
                            title={tooltipContent as any}
                            placement="bottom"
                          >
                            <div
                              className={classNames(
                                'roster-week-navigation__duty-badge',
                                { 
                                  'roster-week-navigation__duty-badge--zero': duty.currentStaff === 0,
                                  'roster-week-navigation__duty-badge--met': duty.isMet 
                                }
                              )}
                            >
                              {duty.icon}
                              <span className="roster-week-navigation__duty-count">
                                {ratioText}
                              </span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  )}
                </FeatureFlag.On>
              </FeatureFlag>
            </DayWrapper>
          )
        )}
      </div>
    </div>
  </RosterNavWrapper>
);
